import type { Today } from "../api";
import { formatEnergy } from "../format";
import { AnimatedNumber, Card, SectionTitle } from "./ui";

/**
 * v2 today tiles.
 *
 * Home shows: a self-sufficiency headline card with a progress ring,
 * plus the PV / verbruik pair (tap-to-drill-in). The battery + grid
 * kWh detail row lives on the Live tab (TodayDetailTiles).
 */

interface TileProps {
  label: string;
  kwh: number;
  accent: string;
  soft: string;
  hint?: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

function Tile({ label, kwh, accent, soft, hint, icon, onClick }: TileProps) {
  return (
    <Card onClick={onClick} className="p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span
          className="w-7 h-7 rounded-xl grid place-items-center"
          style={{ background: soft, color: accent }}
        >
          {icon}
        </span>
        <span className="text-xs font-medium text-ink-mute">{label}</span>
      </div>
      <AnimatedNumber
        value={kwh}
        format={(v) => formatEnergy(v)}
        className="display-num text-[22px] leading-none text-ink"
      />
      {hint && <div className="text-[10px] text-ink-mute">{hint}</div>}
    </Card>
  );
}

interface Props {
  today: Today;
  onMetricTap?: (metric: "pv" | "load") => void;
}

export function TodayTiles({ today, onMetricTap }: Props) {
  // The backend's per-hour share: consumption that did not come from
  // the grid in that hour, so the battery counts. The old local
  // pv / (pv + grid_in) could not see the battery: 52 % on a morning
  // with zero import (2026-10-22).
  const selfSuff = today.self_sufficiency_pct;

  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>Tot nu toe vandaag</SectionTitle>

      {/* Headline: self-sufficiency with progress ring */}
      <Card className="p-4 flex items-center gap-4">
        <SelfSuffRing pct={selfSuff} />
        <div className="min-w-0">
          <div className="text-xs font-medium text-ink-mute">Zelfvoorzienend</div>
          <AnimatedNumber
            value={selfSuff}
            format={(v) => `${Math.round(Math.max(0, Math.min(100, v)))}%`}
            className="display-num text-3xl text-accent leading-tight"
          />
          <div className="text-[11px] text-ink-mute">
            aandeel verbruik dat niet van het net kwam
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <Tile
          label="Opgewekt"
          kwh={today.pv_kwh}
          accent="var(--color-sun)"
          soft="var(--color-sun-soft)"
          icon={<SunIcon />}
          onClick={onMetricTap && (() => onMetricTap("pv"))}
        />
        <Tile
          label="Verbruikt"
          kwh={today.load_kwh}
          accent="var(--color-home)"
          soft="var(--color-home-soft)"
          icon={<HomeIcon />}
          onClick={onMetricTap && (() => onMetricTap("load"))}
        />
      </div>
    </section>
  );
}

function SelfSuffRing({ pct }: { pct: number }) {
  const R = 30;
  const c = 2 * Math.PI * R;
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <svg width={76} height={76} viewBox="0 0 76 76" className="shrink-0" aria-hidden>
      <circle cx={38} cy={38} r={R} fill="none" stroke="var(--color-card-soft)" strokeWidth={7} />
      <circle
        cx={38} cy={38} r={R} fill="none"
        stroke="var(--color-accent)" strokeWidth={7} strokeLinecap="round"
        strokeDasharray={`${(c * clamped) / 100} ${c}`}
        transform="rotate(-90 38 38)"
        style={{ transition: "stroke-dasharray 600ms ease" }}
      />
      <g transform="translate(27, 27)" style={{ color: "var(--color-accent)" }}>
        <LeafIcon />
      </g>
    </svg>
  );
}

interface DetailProps {
  today: Today;
  onMetricTap?: (metric: "battery_in" | "battery_out" | "grid_in" | "grid_out") => void;
}

/**
 * The battery + grid detail tiles - rendered on the Live tab below the
 * live-power chart. Tap-to-drill-in when `onMetricTap` is supplied.
 */
export function TodayDetailTiles({ today, onMetricTap }: DetailProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <Tile
          label="Opgeladen"
          kwh={today.to_battery_kwh}
          accent="var(--color-accent)"
          soft="var(--color-accent-soft)"
          icon={<DownIcon />}
          hint="naar batterij"
          onClick={onMetricTap && (() => onMetricTap("battery_in"))}
        />
        <Tile
          label="Ontladen"
          kwh={today.from_battery_kwh}
          accent="var(--color-accent)"
          soft="var(--color-accent-soft)"
          icon={<UpIcon />}
          hint="uit batterij"
          onClick={onMetricTap && (() => onMetricTap("battery_out"))}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Tile
          label="Geïmporteerd"
          kwh={today.grid_in_kwh}
          accent="var(--color-grid)"
          soft="var(--color-grid-soft)"
          icon={<DownIcon />}
          hint="afgenomen van net"
          onClick={onMetricTap && (() => onMetricTap("grid_in"))}
        />
        <Tile
          label="Geëxporteerd"
          kwh={today.grid_out_kwh}
          accent="var(--color-grid)"
          soft="var(--color-grid-soft)"
          icon={<UpIcon />}
          hint="teruggeleverd"
          onClick={onMetricTap && (() => onMetricTap("grid_out"))}
        />
      </div>
    </div>
  );
}

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function SunIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" {...stroke}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}
function HomeIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" {...stroke}>
      <path d="M3 12l9-8 9 8" />
      <path d="M5 10v10h14V10" />
    </svg>
  );
}
function LeafIcon() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" {...stroke}>
      <path d="M11 20A7 7 0 019.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6" />
    </svg>
  );
}
function DownIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" {...stroke}>
      <path d="M12 5v14M5 12l7 7 7-7" />
    </svg>
  );
}
function UpIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" {...stroke}>
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}
